import { useState } from "react";
import { Thermometer, ImageOff } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Dialog, DialogContent } from "./ui/dialog";
import { SevBadge, ScoreRing } from "./Bits";
import { photoSrc } from "../lib/api";
import { liveSummary } from "../lib/summary";
import { useBranding } from "../lib/branding";

const LIMITS = [
  "Visual and functional inspection of accessible areas on the inspection date. Concealed elements were not opened or destructively tested.",
  "Visual/functional electrical inspection does not constitute electrical code compliance, load capacity or detailed electrical engineering testing unless separately commissioned.",
  "Thermal imaging findings are temperature anomalies/indicators, not definitive proof of concealed defects.",
  "Cracks are reported as visible observations only. Where noted, assessment by a qualified structural engineer is recommended before rectification.",
  "AI-assisted suggestions were reviewed and approved by the inspector before inclusion.",
];

const Photo = ({ id, onOpen, testid }) => {
  const src = photoSrc(id);
  if (!src)
    return (
      <div className="grid aspect-[4/3] place-items-center rounded-md bg-slate-100 text-slate-400">
        <ImageOff className="h-5 w-5" />
      </div>
    );
  return (
    <button
      onClick={() => onOpen(src)}
      data-testid={testid}
      className="block overflow-hidden rounded-md border border-slate-200"
    >
      <img
        src={src}
        alt="defect evidence"
        className="aspect-[4/3] w-full object-cover transition-transform hover:scale-105"
        loading="lazy"
      />
    </button>
  );
};

export function DefectCard({ d, onOpen }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white"
      data-testid={`report-defect-${d.number}`}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="font-mono text-xs font-bold">DEFECT #{d.number}</span>
        <SevBadge sev={d.severity} />
      </div>
      <div className="grid gap-4 p-4 md:grid-cols-[220px_1fr]">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
          {(d.photos || []).length ? (
            d.photos
              .slice(0, 2)
              .map((p) => <Photo key={p} id={p} onOpen={onOpen} />)
          ) : (
            <Photo id={null} />
          )}
        </div>
        <div className="text-sm">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {d.room} · {d.category}
            {d.item ? ` · ${d.item}` : ""}
          </div>
          <div className="mt-1 font-display text-lg font-bold">{d.name}</div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
            Observation
          </div>
          <p className="text-slate-800">{d.remark}</p>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
            Recommendation
          </div>
          <p className="text-slate-700">{d.recommendation}</p>
          {d.measurement && (
            <p className="mt-2 text-slate-600">
              <b>Measurement:</b> {d.measurement}
            </p>
          )}
          {d.tester_result && (
            <p className="mt-1 text-slate-600">
              <b>Electrical tester:</b>{" "}
              <span className="font-mono">{d.tester_result}</span>
            </p>
          )}
          {d.note && (
            <p className="mt-1 text-slate-600">
              <b>Inspector note:</b> {d.note}
            </p>
          )}
          {d.thermal && (
            <div className="mt-4 rounded-md border border-orange-200 bg-orange-50/50 p-3">
              <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-orange-700">
                <Thermometer className="h-3.5 w-3.5" /> Thermal ·{" "}
                {d.thermal.area}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Photo id={d.thermal.photo_id} onOpen={onOpen} />
                  <div className="mt-1 text-[10px] text-slate-500">
                    Thermal image
                  </div>
                </div>
                <div>
                  <Photo id={d.thermal.normal_photo_id} onOpen={onOpen} />
                  <div className="mt-1 text-[10px] text-slate-500">
                    Normal image
                  </div>
                </div>
              </div>
              <p className="mt-2 text-xs">
                <b>Reading:</b> {d.thermal.temperature}
              </p>
              <p className="text-xs">{d.thermal.observation}</p>
              <p className="text-xs italic text-slate-600">
                {d.thermal.recommendation}
              </p>
              <p className="mt-1 text-[10px] text-slate-500">
                Temperature anomaly indicator only — not definitive proof of
                concealed defects.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const RE = {
  RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-50 text-amber-700 border-amber-200",
  NOT_RESOLVED: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function ReportView({ ins, actions }) {
  const [lb, setLb] = useState(null);
  const b = useBranding();
  const config = ins.report_config || {};
  const systemSummary = liveSummary(
    ins,
    config.hidden_room_ids || [],
    config.hidden_finding_ids || [],
  );
  const finalScore = config.final_score;
  const s = { ...systemSummary, score: finalScore ?? systemSummary.score };
  const isScoreOverridden =
    finalScore !== null &&
    finalScore !== undefined &&
    finalScore !== systemSummary.score;
  const scoringExplanation =
    config.scoring_explanation ||
    "Total Checklist Items = Scorable Items + N/T + N/A. N/A and N/T are excluded " +
      "from the score calculation.";
  const roomStatuses = config.room_status_overrides || {};
  const p = ins.property || {};
  const sections = Object.fromEntries(
    (config.sections || []).map((section) => [section.id, section]),
  );
  const visible = (id) => sections[id]?.visible !== false;
  const title = (id, fallback) => sections[id]?.title || fallback;
  const visibleRooms = s.rooms;
  const activeDefects = (ins.defects || []).filter(
    (defect) =>
      !new Set(config.hidden_finding_ids || []).has(defect.defect_id) &&
      !new Set(config.hidden_room_ids || []).has(defect.room_id),
  );
  const priorityById = Object.fromEntries(
    activeDefects.map((defect) => [defect.defect_id, defect]),
  );
  const priorities = config.priority_override
    ? (config.priority_finding_ids || [])
        .map((id) => priorityById[id])
        .filter(Boolean)
    : s.priority;
  const roomsWithDefects = visibleRooms.filter(
    (room) =>
      room.defects ||
      room.major ||
      room.minor ||
      room.cosmetic ||
      room.observation,
  );
  const savedExecutive = ins.final_notes?.executive_summary || "";
  const isSystemExecutive =
    ins.final_notes?.executive_summary_mode === "system" ||
    savedExecutive.includes("observations were recorded:");
  const executiveText = isSystemExecutive
    ? `Checklist outcomes: ${s.totalObservations} recorded findings — ${s.counts.MAJOR} major, ${s.counts.MINOR} minor, ${s.counts.COSMETIC} cosmetic and ${s.counts.OBSERVATION} observations.`
    : savedExecutive || "Summary will appear once the inspection is completed.";
  return (
    <div className="mx-auto max-w-5xl" data-testid="report-view">
      <div className="overflow-hidden rounded-xl bg-slate-900 text-white">
        <div className="grid-bg-dark p-6 sm:p-10">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <div>
              <div className="overline text-teal-300">
                {ins.type === "reinspection"
                  ? "Re-inspection report"
                  : "Home inspection report"}
              </div>
              <h1
                className="mt-3 font-display text-3xl font-extrabold sm:text-4xl"
                data-testid="report-property-title"
              >
                {p.project} · {p.flat}
              </h1>
              <p className="mt-2 text-slate-400">
                {p.location} · {p.bhk} BHK {p.property_type} · {p.super_area}{" "}
                sq.ft.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs text-slate-400">
                <span>
                  REPORT{" "}
                  <b className="text-white" data-testid="report-id">
                    {ins.inspection_id}
                  </b>
                </span>
                <span>
                  DATE <b className="text-white">{ins.inspection_date}</b>
                </span>
                <span>
                  INSPECTOR <b className="text-white">{ins.inspector_name}</b>
                </span>
              </div>
            </div>
            <div
              className="rounded-xl bg-white p-4 text-slate-900"
              data-testid="customer-report-score-card"
            >
              <ScoreRing score={s.score} />
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {["MAJOR", "MINOR", "COSMETIC", "OBSERVATION"].map((k) => (
              <div
                key={k}
                className="rounded-md border border-slate-700 bg-slate-800/60 px-4 py-3"
                data-testid={`report-count-${k.toLowerCase()}`}
              >
                <div className="font-mono text-[10px] tracking-widest text-slate-400">
                  {k}
                </div>
                <div className="font-display text-2xl font-extrabold">
                  {s.counts[k] || 0}
                </div>
              </div>
            ))}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap gap-2 border-t border-slate-800 bg-slate-950 p-4">
            {actions}
          </div>
        )}
      </div>

      {(visible("executive_summary") || visible("property_details")) && (
        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          {visible("executive_summary") && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2">
              <div className="overline mb-2">
                {title("executive_summary", "Executive summary")}
              </div>
              <p
                className="text-slate-700"
                data-testid="report-executive-summary"
              >
                {executiveText}
              </p>
              <div className="mt-6 overline mb-2">Top priority issues</div>
              <ol className="space-y-2">
                {priorities.map((d, i) => (
                  <li
                    key={d.defect_id || i}
                    className="flex items-center gap-3 text-sm"
                  >
                    <span className="font-mono text-xs text-slate-400">
                      {i + 1}.
                    </span>
                    <span className="font-medium">{d.name}</span>
                    <span className="text-slate-500">— {d.room}</span>
                    <span className="ml-auto">
                      <SevBadge sev={d.severity} />
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {visible("property_details") && (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="overline mb-3">
                {title("property_details", "Property details")}
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {[
                  ["Customer", ins.customer?.name],
                  ["Builder", p.builder],
                  ["Tower", p.tower],
                  ["Floor", `${p.floor ?? "-"} / ${p.total_floors ?? "-"}`],
                  ["Carpet", `${p.carpet_area ?? "-"} sq.ft.`],
                  ["Status", p.status],
                  ["Possession", p.possession_status],
                  ["Service", ins.service_type],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
                      {k}
                    </dt>
                    <dd className="font-medium">{v || "-"}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </section>
      )}

      {visible("score") && (
        <section
          className="mt-8 border-y border-slate-200 py-6"
          data-testid="report-checklist-breakdown"
        >
          <div className="overline mb-4">Checklist outcome breakdown</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-5">
            {[
              ["Total checklist items", s.items.total],
              ["Scorable items", s.scorableTotal],
              ["Passed", s.items.PASS],
              ["Major", s.items.MAJOR],
              ["Minor", s.items.MINOR],
              ["Cosmetic", s.items.COSMETIC],
              ["Observation", s.items.OBSERVATION],
              ["Not assessed", s.items.UNASSESSED],
              ["N/T — excluded", s.items.NT],
              ["N/A — excluded", s.items.NA],
            ].map(([label, value]) => (
              <div
                key={label}
                data-testid={`report-breakdown-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {label}
                </div>
                <div className="mt-1 font-display text-xl font-bold">
                  {value}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-2 border-t border-slate-200 pt-5 text-sm text-slate-600">
            {isScoreOverridden && (
              <p data-testid="report-score-override-disclosure">
                <b>Calculated score:</b> {systemSummary.score}/100 ·{" "}
                <b>Final score:</b> {s.score}/100 · <b>Status:</b> Admin
                Override
                {config.score_override_note && (
                  <>
                    <br />
                    <b>Admin note:</b> {config.score_override_note}
                  </>
                )}
              </p>
            )}
            <p data-testid="report-scoring-explanation">
              <b>Score method:</b> {scoringExplanation}
            </p>
          </div>
        </section>
      )}

      {ins.type === "reinspection" && visible("reinspection") && (
        <section className="mt-8">
          <div className="overline mb-3">Before vs after</div>
          <div className="space-y-3">
            {(ins.original_defects || []).map((d) => (
              <div
                key={d.defect_id}
                className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[1fr_1fr_1.2fr]"
                data-testid={`compare-${d.number}`}
              >
                <div>
                  <div className="mb-1 font-mono text-[10px] text-slate-400">
                    BEFORE
                  </div>
                  <Photo id={d.photos?.[0]} onOpen={setLb} />
                </div>
                <div>
                  <div className="mb-1 font-mono text-[10px] text-slate-400">
                    AFTER
                  </div>
                  <Photo id={d.new_photos?.[0]} onOpen={setLb} />
                </div>
                <div className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold">
                      {d.number}
                    </span>
                    <span
                      className={`rounded border px-2 py-0.5 font-mono text-[10px] font-bold ${RE[d.re_status] || "bg-slate-100 text-slate-600"}`}
                    >
                      {(d.re_status || "NOT CHECKED").replace("_", " ")}
                    </span>
                  </div>
                  <div className="mt-1 font-semibold">
                    {d.name} — {d.room}
                  </div>
                  <p className="mt-2 text-slate-600">
                    <b>Previous:</b> {d.remark}
                  </p>
                  {d.re_note && (
                    <p className="mt-1 text-slate-600">
                      <b>Now:</b> {d.re_note}
                    </p>
                  )}
                  {d.builder_fix && (
                    <p className="mt-1 text-emerald-700">
                      <b>Builder:</b>{" "}
                      {d.builder_fix.note || d.builder_fix.status}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {visible("detailed_observations") && (
        <section className="mt-8">
          <div className="overline mb-3">
            {title("detailed_observations", "Room-wise observations")}
          </div>
          <Accordion
            type="multiple"
            defaultValue={roomsWithDefects.slice(0, 2).map((r) => r.room_id)}
            className="space-y-2"
          >
            {visibleRooms.map((r) => (
              <AccordionItem
                key={r.room_id}
                value={r.room_id}
                className="rounded-lg border border-slate-200 bg-white px-4"
                data-testid={`report-room-${r.room_id}`}
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex flex-1 flex-wrap items-center gap-3 pr-3 text-left">
                    <span className="font-semibold">{r.name}</span>
                    <span
                      className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-500"
                      data-testid={`report-room-status-${r.room_id}`}
                    >
                      {roomStatuses[r.room_id] ||
                        (r.major || r.minor ? "Attention" : "Clear")}
                    </span>
                    <span className="ml-auto flex gap-1.5">
                      {r.major > 0 && (
                        <span className="rounded bg-rose-600 px-1.5 font-mono text-[10px] font-bold text-white">
                          {r.major}
                        </span>
                      )}
                      {r.minor > 0 && (
                        <span className="rounded bg-amber-500 px-1.5 font-mono text-[10px] font-bold text-white">
                          {r.minor}
                        </span>
                      )}
                      {r.cosmetic > 0 && (
                        <span className="rounded bg-blue-600 px-1.5 font-mono text-[10px] font-bold text-white">
                          {r.cosmetic}
                        </span>
                      )}
                      {r.observation > 0 && (
                        <span className="rounded bg-violet-600 px-1.5 font-mono text-[10px] font-bold text-white">
                          {r.observation}
                        </span>
                      )}
                      {!(r.major || r.minor || r.cosmetic || r.observation) && (
                        <span className="font-mono text-[10px] text-emerald-600">
                          NO DEFECTS
                        </span>
                      )}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-4">
                  {activeDefects
                    .filter((d) => d.room_id === r.room_id)
                    .map((d) => (
                      <DefectCard key={d.defect_id} d={d} onOpen={setLb} />
                    ))}
                  {!r.defects &&
                    (r.major || r.minor || r.cosmetic || r.observation) && (
                      <p className="text-sm text-slate-500">
                        Checklist findings are recorded for this room. No
                        detailed finding card was added.
                      </p>
                    )}
                  {!r.defects &&
                    !(r.major || r.minor || r.cosmetic || r.observation) && (
                      <p className="text-sm text-slate-500">
                        {r.total} items checked. No defects recorded.
                      </p>
                    )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {(visible("limitations") || visible("disclaimer")) && (
        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          {visible("limitations") && (
            <>
              <div className="overline mb-3">
                {title("limitations", "Inspection limitations")}
              </div>
              <ul
                className="space-y-2 text-sm text-slate-600"
                data-testid="report-limitations"
              >
                {(b?.limitations || LIMITS).map((l) => (
                  <li key={l}>— {l}</li>
                ))}
              </ul>
            </>
          )}
          {visible("disclaimer") && (
            <>
              <div className="overline mb-3 mt-6">
                {title("disclaimer", "Disclaimer")}
              </div>
              <ul
                className="space-y-2 text-sm text-slate-600"
                data-testid="report-disclaimer"
              >
                {(b?.disclaimer || []).map((l) => (
                  <li key={l}>— {l}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
      <Dialog open={!!lb} onOpenChange={() => setLb(null)}>
        <DialogContent className="max-w-4xl p-2" data-testid="photo-lightbox">
          {lb && <img src={lb} alt="evidence" className="w-full rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
